import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    IconButton,
    Modal,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import ProductDetailsCard from "../../components/Cards/ProductDetailsCard";
import USDAExpertSidebar from "../global/USDAExpertSidebar"; // Import the USDAExpertSidebar
import RecipeSuggestor from "../../components/scene-ai/RecipeSuggestor"; // Import RecipeSuggestor component
import figLogo from "../../assets/fig_CROP.png"; // FIG Logo

const USDACNDBSearch = () => {
    // Create a light theme
    const lightTheme = createTheme({
        palette: {
            mode: "light",
            primary: { main: "#1976d2" },
            background: { default: "#f4f6f8", paper: "#ffffff" },
            text: { primary: "#000000", secondary: "#555555" },
        },
    });

    // State variables
    const [query, setQuery] = useState("");
    // Search type can be: "descriptor", "cnCode", or "category"
    const [searchType, setSearchType] = useState("descriptor");
    const [allProducts, setAllProducts] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [recipeSuggestorOpen, setRecipeSuggestorOpen] = useState(false);
    const [selectedFdcId, setSelectedFdcId] = useState(null);

    // API endpoint
    const CNDB_API_URL = "https://api.swancreate.com/api/llm/CTX_CNDB_Full";

    // -------------------------------------
    // 1) INITIAL LOAD: CN_Code ~ "10000"
    // -------------------------------------
    // On mount, fetch items where 'Cn_code' contains "10000"
    const fetchData = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(`${CNDB_API_URL}?field=cn_code&value=10000`);
            const products = response.data || [];

            // If you want to see all items, comment out the line below
            // or skip the top 10 approach:
            // const uniqueProducts = getTop10UniqueDescriptors(products);
            // setAllProducts(uniqueProducts);
            // setResults(uniqueProducts);

            // Otherwise, store full results:
            setAllProducts(products);
            setResults(products);
        } catch (err) {
            setError("Failed to fetch product data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    /**
     * A helper to get top 10 distinct descriptors
     * Not necessarily used, but kept to avoid removing lines
     */
    const getTop10UniqueDescriptors = (products) => {
        const uniqueMap = new Map();
        for (const product of products) {
            if (!uniqueMap.has(product.Descriptor)) {
                uniqueMap.set(product.Descriptor, product);
            }
            if (uniqueMap.size === 10) break;
        }
        return Array.from(uniqueMap.values());
    };

    // -------------------------------------
    // 2) SERVER-SIDE SEARCH
    // -------------------------------------
    // Instead of local filtering, we call the API with field & value each time.
    const handleSearch = async () => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) {
            setError("Please enter a search term.");
            return;
        }
        setError("");
        setLoading(true);

        // Convert from "descriptor"/"cnCode"/"category" to API param
        let fieldParam = "";
        if (searchType === "descriptor") {
            fieldParam = "descriptor";
        } else if (searchType === "cnCode") {
            fieldParam = "cn_code";
        } else if (searchType === "category") {
            fieldParam = "category";
        }

        try {
            const response = await axios.get(
                `${CNDB_API_URL}?field=${fieldParam}&value=${encodeURIComponent(trimmedQuery)}`
            );
            setResults(response.data || []);
        } catch (err) {
            console.error("Search error:", err.message);
            setError("Search failed. Please try again.");
        } finally {
            setLoading(false);
        }

        // (Commented out local filtering)
        /*
        const filtered = allProducts.filter((product) => {
            if (searchType === "descriptor") {
                return (
                    product.Descriptor &&
                    product.Descriptor.toLowerCase().includes(trimmedQuery.toLowerCase())
                );
            } else if (searchType === "cnCode") {
                return (
                    product.Cn_code &&
                    String(product.Cn_code).toLowerCase().includes(trimmedQuery.toLowerCase())
                );
            } else if (searchType === "category") {
                return (
                    product.Category_description &&
                    product.Category_description.toLowerCase().includes(trimmedQuery.toLowerCase())
                );
            }
            return false;
        });
        setResults(filtered);
        */
    };

    // -------------------------------------
    // 3) Actions & UI
    // -------------------------------------
    const handleRowClick = (product) => {
        setSelectedProduct(product);
    };

    const handleSuggestRecipe = (product) => {
        setSelectedFdcId(product.Fdc_id);
        setRecipeSuggestorOpen(true);
    };

    const handleCloseRecipeSuggestor = () => {
        setRecipeSuggestorOpen(false);
        setSelectedFdcId(null);
    };

    const handleClose = () => {
        setSelectedProduct(null);
    };

    return (
        <ThemeProvider theme={lightTheme}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    height: "100vh",
                    overflow: "hidden",
                }}
            >
                {/* Main Content Area */}
                <Box
                    sx={{
                        flex: isSidebarOpen ? 1 : 1.5,
                        padding: "20px",
                        backgroundColor: lightTheme.palette.background.default,
                        overflowY: "auto",
                    }}
                >
                    <IconButton
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        sx={{
                            position: "absolute",
                            top: "16px",
                            right: "16px",
                            backgroundColor: lightTheme.palette.primary.main,
                            borderRadius: "50%",
                            width: "48px",
                            height: "48px",
                        }}
                    >
                        <img
                            src={figLogo}
                            alt="FIG Logo"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                            }}
                        />
                    </IconButton>

                    {/* Page title */}
                    <Typography variant="h4" gutterBottom color={lightTheme.palette.text.primary}>
                        USDA Child Nutrition Database Search
                    </Typography>

                    {/* Search Instructions */}
                    <Paper
                        sx={{
                            padding: "20px",
                            marginBottom: "20px",
                            backgroundColor: lightTheme.palette.background.paper,
                        }}
                    >
                        <Typography variant="h6" gutterBottom color={lightTheme.palette.text.primary}>
                            How to Use This Page
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            We load items where CN Code contains "10000" by default. Enter a search
                            term, pick a search type, and hit Search to query the API.
                        </Typography>
                    </Paper>

                    {/* Search UI */}
                    <Box sx={{ marginBottom: "20px" }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ color: "darkblue" }}>
                                Search Type
                            </FormLabel>
                            <RadioGroup
                                row
                                aria-label="searchType"
                                name="searchType"
                                value={searchType}
                                onChange={(e) => setSearchType(e.target.value)}
                            >
                                <FormControlLabel
                                    value="descriptor"
                                    control={<Radio sx={{ color: "darkblue" }} />}
                                    label="Descriptor"
                                    sx={{ color: "darkblue" }}
                                />
                                <FormControlLabel
                                    value="cnCode"
                                    control={<Radio sx={{ color: "darkblue" }} />}
                                    label="CN Code"
                                    sx={{ color: "darkblue" }}
                                />
                                <FormControlLabel
                                    value="category"
                                    control={<Radio sx={{ color: "darkblue" }} />}
                                    label="Category"
                                    sx={{ color: "darkblue" }}
                                />
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            label={
                                searchType === "descriptor"
                                    ? "Descriptor"
                                    : searchType === "cnCode"
                                        ? "CN Code"
                                        : "Category"
                            }
                            variant="outlined"
                            fullWidth
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            sx={{ marginTop: 2 }}
                        />

                        <Box sx={{ marginTop: 2 }}>
                            <Button variant="contained" color="primary" onClick={handleSearch}>
                                Search
                            </Button>
                        </Box>
                    </Box>

                    {/* Loading & Error */}
                    {loading && <CircularProgress />}
                    {error && (
                        <Typography variant="body2" color="error" sx={{ marginTop: 2 }}>
                            {error}
                        </Typography>
                    )}

                    {/* Results Table */}
                    {results.length > 0 && (
                        <TableContainer
                            component={Paper}
                            sx={{
                                marginTop: "20px",
                                backgroundColor: lightTheme.palette.background.paper,
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: lightTheme.palette.primary.main }}>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            Descriptor
                                        </TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            CN Code
                                        </TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            Category
                                        </TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            FDC ID
                                        </TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((product, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                cursor: "pointer",
                                                backgroundColor:
                                                    index % 2 === 0
                                                        ? lightTheme.palette.action.hover
                                                        : "inherit",
                                            }}
                                        >
                                            <TableCell>{product.Descriptor}</TableCell>
                                            <TableCell>{product.Cn_code}</TableCell>
                                            <TableCell>{product.Category_description}</TableCell>
                                            <TableCell>{product.Fdc_id}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleRowClick(product)}
                                                    >
                                                        View Product Information
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={() => handleSuggestRecipe(product)}
                                                    >
                                                        Suggest Recipe
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {selectedProduct && (
                        <ProductDetailsCard productData={selectedProduct} onClose={handleClose} />
                    )}
                </Box>

                {/* USDAExpertSidebar */}
                {isSidebarOpen && (
                    <Box
                        sx={{
                            width: "400px",
                            flexShrink: 0,
                            backgroundColor: lightTheme.palette.background.paper,
                            boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.2)",
                        }}
                    >
                        <USDAExpertSidebar />
                    </Box>
                )}
            </Box>

            {/* RecipeSuggestor Modal */}
            <Modal
                open={recipeSuggestorOpen}
                onClose={handleCloseRecipeSuggestor}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "white",
                        padding: 4,
                        borderRadius: 2,
                        boxShadow: 24,
                        width: "80%",
                        maxWidth: "800px",
                    }}
                >
                    <RecipeSuggestor fdcId={selectedFdcId} onClose={handleCloseRecipeSuggestor} />
                </Box>
            </Modal>
        </ThemeProvider>
    );
};

export default USDACNDBSearch;
